import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, InputNumber, Empty, message, Popconfirm, Divider } from 'antd';
import { DeleteOutlined, ShoppingOutlined } from '@ant-design/icons';
import { cartAPI } from '../services/api';
import { requireAuth } from '../utils/auth';
import './Cart.css';

function Cart() {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});

  useEffect(() => {
    if (!requireAuth(navigate)) return;
    loadCart();
  }, []);

  const loadCart = async () => {
    setLoading(true);
    try {
      const res = await cartAPI.getCart();
      setCartItems(res.data.items);
    } catch (error) {
      message.error('加载购物车失败');
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = async (cartId, newQuantity) => {
    setUpdating(prev => ({ ...prev, [cartId]: true }));
    try {
      await cartAPI.updateCartItem(cartId, { quantity: newQuantity });
      loadCart();
      window.dispatchEvent(new Event('cartUpdate'));
    } catch (error) {
      message.error(error.message || '更新失败');
    } finally {
      setUpdating(prev => ({ ...prev, [cartId]: false }));
    }
  };

  const handleRemove = async (cartId) => {
    try {
      await cartAPI.removeFromCart(cartId);
      message.success('已移除');
      loadCart();
      window.dispatchEvent(new Event('cartUpdate'));
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      message.warning('购物车为空');
      return;
    }
    navigate('/checkout');
  };

  const calculateTotal = () => {
    return cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0).toFixed(2);
  };

  if (loading) {
    return <div className="loading-container">加载中...</div>;
  }

  return (
    <div className="cart-container">
      <h1 className="page-title">购物车</h1>

      {cartItems.length === 0 ? (
        <Card>
          <Empty
            description="购物车还是空的"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button
              type="primary"
              icon={<ShoppingOutlined />}
              onClick={() => navigate('/mall')}
            >
              去逛逛
            </Button>
          </Empty>
        </Card>
      ) : (
        <div className="cart-content">
          <Card className="cart-items-card">
            <div className="cart-header">
              <div className="header-item product-col">商品信息</div>
              <div className="header-item price-col">单价</div>
              <div className="header-item quantity-col">数量</div>
              <div className="header-item subtotal-col">小计</div>
              <div className="header-item action-col">操作</div>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            {cartItems.map(item => (
              <div key={item.id} className="cart-item">
                <div className="item-product product-col">
                  <img
                    src={item.main_image || '/images/default-product.jpg'}
                    alt={item.name}
                    onError={(e) => { e.target.src = '/images/default-product.jpg'; }}
                  />
                  <div className="product-info">
                    <div className="product-name">{item.name}</div>
                    <div className="product-type">{item.type === 'physical' ? '实物' : '虚拟'}</div>
                  </div>
                </div>

                <div className="item-price price-col">
                  <span className="price">¥{item.price}</span>
                </div>

                <div className="item-quantity quantity-col">
                  <InputNumber
                    min={1}
                    max={item.stock}
                    value={item.quantity}
                    onChange={(value) => handleQuantityChange(item.id, value)}
                    disabled={updating[item.id]}
                  />
                  <span className="stock-hint">库存: {item.stock}</span>
                </div>

                <div className="item-subtotal subtotal-col">
                  <span className="subtotal">¥{(item.price * item.quantity).toFixed(2)}</span>
                </div>

                <div className="item-action action-col">
                  <Popconfirm
                    title="确定要移除这件商品吗？"
                    onConfirm={() => handleRemove(item.id)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                    >
                      删除
                    </Button>
                  </Popconfirm>
                </div>
              </div>
            ))}
          </Card>

          <Card className="cart-summary-card">
            <h3>订单摘要</h3>
            <div className="summary-item">
              <span>商品件数:</span>
              <span>{cartItems.length} 件</span>
            </div>
            <div className="summary-item">
              <span>商品总数:</span>
              <span>{cartItems.reduce((sum, item) => sum + item.quantity, 0)} 个</span>
            </div>
            <Divider />
            <div className="summary-total">
              <span>合计:</span>
              <span className="total-price">¥{calculateTotal()}</span>
            </div>
            <Button
              type="primary"
              size="large"
              block
              onClick={handleCheckout}
            >
              去结算
            </Button>
          </Card>
        </div>
      )}
    </div>
  );
}

export default Cart;
